import { useEffect, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to detect when strategems are played and play appropriate sound effects
 * - shrink.mp3 for strategems that shrink units
 * - grow.mp3 for strategems that grow units
 * - destroy.mp3 for strategems that destroy units
 */
export const useStrategemSoundEffects = (gameState, userId) => {
  const previousCompletedStrategemsRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  // Mapping of strategem IDs to their sound effects
  const strategemSoundMapping = {
    // Shrink strategems
    shrink: new Set([53, 180, 183]), // Neurotox Pulse, Touch of darkness, Growing Darkness
    
    // Grow strategems
    grow: new Set([62, 182, 224, 227]), // Hive Surge, Gift of the Rift, Divine Favor, Praise the Gods
    
    // Destroy / direct-damage strategems (including general damage)
    destroy: new Set([
      51,  // Culling Protocol
      54,  // Repurposed Warhead
      55,  // Vein Sever
      57,  // Swarm Collapse
      59,  // Fracture Minds
      60,  // Counter Strike
      63,  // Pulse Shockwave
      112, // Storm of Missiles
      116, // Field Dominator
      185, // Shadow Bargain - deals 3 damage to enemy general
      179, // Surprise Attack (can destroy)
      181, // Tactical Breach
      197  // Unethical Experiment
    ])
  };

  // Custom sound mappings for specific strategem IDs
  const customSoundMapping = {
    115: '/sounds/stinger.mp3',   // Stinger Unit Alpha
    226: '/sounds/mummy.mp3',     // Embalm
    313: '/sounds/duplicate.mp3', // Duplicate
    316: '/sounds/missile_barrage.mp3', // Missile Barrage
    317: '/sounds/missile.mp3',   // Guided Missile
    319: '/sounds/fire.mp3',       // Inferno Drop
    321: '/sounds/missile.mp3'     // Precision Strike
  };

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    // Skip detection on first render
    if (isFirstRenderRef.current) {
      console.log(`🎯 First render - initializing strategem tracking (skipping sound)`);
      isFirstRenderRef.current = false;
      
      // Build initial set of completed strategems
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      allPlayerIds.forEach(playerId => {
        const player = gameState.state[playerId];
        if (player?.completedOrders) {
          player.completedOrders.forEach(strategem => {
            if (strategem && strategem.instanceId && strategem.type === 'Strategem') {
              const key = `${playerId}_${strategem.instanceId}`;
              previousCompletedStrategemsRef.current.add(key);
            }
          });
        }
      });
      return;
    }

    const allPlayerIds = gameState.players || Object.keys(gameState.state);
    const newlyPlayedStrategems = [];

    // Detect newly completed strategems (moved from hand to completedOrders)
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach(strategem => {
        if (strategem && strategem.instanceId && strategem.type === 'Strategem') {
          const key = `${playerId}_${strategem.instanceId}`;
          
          // If this strategem wasn't in completedOrders before, it was just played
          if (!previousCompletedStrategemsRef.current.has(key)) {
            newlyPlayedStrategems.push({
              strategem,
              playerId,
              key
            });
            console.log(`🎯 STRATEGEM PLAYED: ${strategem.name} (ID: ${strategem.id}) by player ${playerId}`);
          }
        }
      });
    });

    // Play sounds for newly played strategems
    if (newlyPlayedStrategems.length > 0) {
      newlyPlayedStrategems.forEach(({ strategem, playerId }, index) => {
        const delay = index * 100; // Stagger sounds if multiple strategems played simultaneously
        
        setTimeout(() => {
          const strategemId = strategem.id;
          let soundPath = null;

          // Check custom sound mapping first (takes priority)
          if (customSoundMapping[strategemId]) {
            soundPath = customSoundMapping[strategemId];
            console.log(`🔊 Playing custom sound for strategem ${strategem.name} (ID: ${strategemId}): ${soundPath}`);
          } else if (strategemSoundMapping.shrink.has(strategemId)) {
            soundPath = '/sounds/shrink.mp3';
            console.log(`🔊 Playing shrink sound for strategem ${strategem.name} (ID: ${strategemId})`);
          } else if (strategemSoundMapping.grow.has(strategemId)) {
            soundPath = '/sounds/grow.mp3';
            console.log(`🔊 Playing grow sound for strategem ${strategem.name} (ID: ${strategemId})`);
          } else if (strategemSoundMapping.destroy.has(strategemId)) {
            soundPath = '/sounds/destroy.mp3';
            console.log(`🔊 Playing destroy sound for strategem ${strategem.name} (ID: ${strategemId})`);
          }

          if (soundPath) {
            try {
              playSoundEffect(soundPath, 0.6); // Play at 60% volume
              console.log(`✅ Strategem sound triggered successfully: ${soundPath}`);
            } catch (error) {
              console.error(`❌ Error playing strategem sound:`, error);
            }
          } else {
            console.log(`⚠️ No sound mapping for strategem ${strategem.name} (ID: ${strategemId})`);
          }
        }, delay);
      });
    }

    // Update the previous completed strategems set
    const currentCompletedStrategems = new Set();
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (player?.completedOrders) {
        player.completedOrders.forEach(strategem => {
          if (strategem && strategem.instanceId && strategem.type === 'Strategem') {
            const key = `${playerId}_${strategem.instanceId}`;
            currentCompletedStrategems.add(key);
          }
        });
      }
    });
    previousCompletedStrategemsRef.current = currentCompletedStrategems;
  }, [gameState, userId]);
};

